import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import UrgencyBar from "../components/UrgencyBar";
import CaseStudyCard from "../components/CaseStudyCard";
import SEOHead from "../components/SEOHead";
import { CASE_STUDIES, CASE_STUDY_CATEGORIES } from "../data/caseStudies";
import { Filter } from "lucide-react";

export default function Cases() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState("All");

  const filteredCases = useMemo(() => {
    if (activeFilter === "All") return CASE_STUDIES;
    return CASE_STUDIES.filter((cs) => cs.category === activeFilter);
  }, [activeFilter]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t('cases_page.title'),
    description: t('cases_page.subtitle'),
    itemListElement: CASE_STUDIES.map((cs, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `https://maxemployee.com/cases/${cs.slug}`,
      name: `${cs.company} — ${cs.headline}`,
    })),
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title="Case Studies | Real Results with Max OS — Max Employee"
        description="See real results from companies using Max OS AI agents. Case studies with metrics on hours saved, revenue gained, and automation ROI."
        url="/cases"
        image="/og-image-homepage.png"
        structuredData={[
          structuredData,
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: t('nav.home'), item: "https://maxemployee.com/" },
              { "@type": "ListItem", position: 2, name: t('cases_page.title'), item: "https://maxemployee.com/cases" },
            ],
          },
        ]}
      />
      <Header />
      <UrgencyBar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-900 bg-black" aria-labelledby="cases-hero-heading">
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#09090B]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-6" aria-hidden="true">
            // {t('cases_section.section_label').replace('// ', '').toUpperCase()}
          </div>
          <h1 id="cases-hero-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-white mb-6">
            {t('cases_section.title_1')}.<br />
            <span className="text-amber-500">{t('cases_section.title_2')}.</span>
          </h1>
          <p className="max-w-2xl text-lg text-zinc-400 leading-relaxed">
            {t('cases_page.subtitle')}
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="sticky top-0 z-30 bg-[#09090B]/95 backdrop-blur-sm border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
            <Filter className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex-shrink-0">
              Filter:
            </span>
            {CASE_STUDY_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 border transition-all duration-200 flex-shrink-0 ${
                  activeFilter === cat
                    ? "bg-amber-500 text-black border-amber-500 font-bold"
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
                }`}
                aria-pressed={activeFilter === cat}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies Grid */}
      <main id="main-content" className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        {filteredCases.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-display text-2xl uppercase text-zinc-600">
              No case studies found.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            role="list"
            aria-label={t('cases_page.title')}
          >
            {filteredCases.map((cs) => (
              <CaseStudyCard key={cs.slug} caseStudy={cs} />
            ))}
          </div>
        )}
      </main>

      {/* CTA */}
      <section className="border-t border-zinc-900 bg-black" aria-labelledby="cases-cta-heading">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-6" aria-hidden="true">
            // {t('final_cta.section_label').replace('// ', '').toUpperCase()}
          </div>
          <h2 id="cases-cta-heading" className="font-display text-3xl md:text-5xl uppercase leading-[0.95] max-w-2xl mx-auto mb-8">
            {t('final_cta.title_1')}<br />
            <span className="text-amber-500">{t('final_cta.title_3')}</span>
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/pricing"
              className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {t('pricing_section.cta')}
            </a>
            <a
              href="mailto:sales@maxemployee.com?subject=Case%20Study%20Inquiry"
              className="inline-flex items-center gap-3 border border-zinc-700 hover:border-zinc-500 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {t('products_page.contact_sales')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
